import { IPlant } from '@/interfaces/powerMarkets/plant/IPlant.ts';
import type {
    IPlantGroupSettingsUpdate,
} from '@/interfaces/powerMarkets/settings/IPlantGroupSettings.ts';
import { ISiteSettings } from '@/interfaces/powerMarkets/settings/ISiteSettings.ts';

/**
 * Compatibility adapter for old Enact Plant Tracking.
 * The legacy fields below are derived from the canonical plant-group settings
 * and can be removed when old Enact no longer reads them.
 */
export interface ILegacyPlantGroupMirrors {
    chosenPlants: Array<IPlant>;
    chosenPlantGroupColour: string;
    plantGroupColourOverride: boolean;
}

function getPreviousChosenPlants (otherSettingsData: Partial<ILegacyPlantGroupMirrors>): Array<IPlant> {
    const chosenPlants = otherSettingsData.chosenPlants;
    if (!Array.isArray(chosenPlants)) return [];
    return chosenPlants.filter((plant): plant is IPlant => (
        plant !== null
        && typeof plant === 'object'
        && typeof plant.id === 'string'
    ));
}

export function buildLegacyPlantGroupMirrors (
    siteSettings: ISiteSettings,
    allPlants: Array<IPlant> = [],
    previousChosenPlants: Array<IPlant> = [],
): ILegacyPlantGroupMirrors {
    const groupName = siteSettings.chosenPlantGroupName;
    if (!groupName) {
        return {
            chosenPlants: [],
            chosenPlantGroupColour: siteSettings.defaultPlantGroupColour,
            plantGroupColourOverride: false,
        };
    }

    const plantIds = siteSettings.chosenPlantsByGroup[groupName] ?? [];
    const plantsById = new Map<string, IPlant>();
    previousChosenPlants.forEach((plant) => {
        if (plant?.id) plantsById.set(plant.id, plant);
    });
    allPlants.forEach((plant) => {
        if (plant?.id) plantsById.set(plant.id, plant);
    });

    // Never persist id-as-name stubs — they overwrite good Cosmo chosenPlants for old Enact.
    const resolvedPlants = plantIds
        .map((plantId) => plantsById.get(plantId))
        .filter((plant): plant is IPlant => plant !== undefined);

    let chosenPlants: Array<IPlant>;
    if (resolvedPlants.length === plantIds.length) {
        chosenPlants = resolvedPlants;
    } else if (allPlants.length === 0) {
        // Static plants not loaded yet — keep prior mirror rather than writing blanks.
        chosenPlants = previousChosenPlants;
    } else {
        // Plants loaded; unknown ids are gone from static — omit, do not stub.
        chosenPlants = resolvedPlants;
    }

    const colourSettings = siteSettings.plantGroupColourOverrideByGroup[groupName];
    return {
        chosenPlants,
        chosenPlantGroupColour: colourSettings?.colour ?? siteSettings.defaultPlantGroupColour,
        plantGroupColourOverride: colourSettings?.override ?? false,
    };
}

/** Apply Spark plant-group fields, overlay legacy mirrors, return post body. */
export function applyPlantGroupSettingsWithLegacyMirrors (
    otherSettingsData: ISiteSettings,
    update: IPlantGroupSettingsUpdate,
    allPlants: Array<IPlant> = [],
): ISiteSettings & ILegacyPlantGroupMirrors {
    if (update.chosenPlantGroupName !== undefined) {
        otherSettingsData.chosenPlantGroupName = update.chosenPlantGroupName;
    }
    if (update.chosenPlantsByGroup !== undefined) {
        otherSettingsData.chosenPlantsByGroup = update.chosenPlantsByGroup;
    }
    if (update.plantGroupColourOverrideByGroup !== undefined) {
        otherSettingsData.plantGroupColourOverrideByGroup = update.plantGroupColourOverrideByGroup;
    }

    const dataWithMirrors = otherSettingsData as ISiteSettings & Partial<ILegacyPlantGroupMirrors>;
    const mirrors = buildLegacyPlantGroupMirrors(
        otherSettingsData,
        allPlants,
        getPreviousChosenPlants(dataWithMirrors),
    );
    Object.assign(otherSettingsData, mirrors);

    return {
        ...otherSettingsData,
        ...mirrors,
    };
}
